import type { MapConfig } from "./config.js";
import { zoomToScale } from "./config.js";

/**
 * Camera controller for smooth map navigation.
 *
 * Features:
 *   - Inertia panning with configurable friction
 *   - Anchor-based zoom easing (world point under cursor stays fixed)
 *   - Pinch-to-zoom with midpoint pivot
 *   - Keyboard navigation
 *   - Bounds clamping with elastic snap-back
 */
export class Camera {
  // Current rendered state
  x = 0;
  y = 0;
  scale = 1;

  // Target state (easing destination)
  targetX = 0;
  targetY = 0;
  targetScale = 1;

  // Zoom anchor: world-space point that stays pinned under the
  // screen-space anchor during animated zoom
  zoomAnchorWX = 0;
  zoomAnchorWY = 0;
  zoomAnchorSX = 0;
  zoomAnchorSY = 0;
  isZooming = false;

  // Inertia velocity
  vx = 0;
  vy = 0;

  // Drag state
  isDragging = false;
  private lastMoveX = 0;
  private lastMoveY = 0;
  private lastMoveTime = 0;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragCamX = 0;
  private dragCamY = 0;

  // Tuning (set from config)
  minScale: number;
  maxScale: number;
  friction: number;
  zoomEasing: number;
  panEasing: number;

  private worldSize: number;
  private boundsPadding: number;
  /** Absolute floor for minScale — the scale at config.minZoom. */
  private minScaleFloor: number;

  constructor(config: MapConfig) {
    this.worldSize = config.worldSize;
    this.boundsPadding = config.boundsPadding;
    this.friction = config.cameraFriction;
    this.zoomEasing = config.cameraZoomEasing;
    this.panEasing = config.cameraPanEasing;
    this.minScaleFloor =
      config.cameraMinScale ?? zoomToScale(config.minZoom, config);
    this.minScale = this.minScaleFloor;
    this.maxScale =
      config.cameraMaxScale ?? zoomToScale(config.maxZoom, config);
  }

  startDrag(mx: number, my: number): void {
    this.isDragging = true;
    this.isZooming = false;
    this.dragStartX = mx;
    this.dragStartY = my;
    this.dragCamX = this.x;
    this.dragCamY = this.y;
    this.targetX = this.x;
    this.targetY = this.y;
    this.vx = 0;
    this.vy = 0;
    this.lastMoveX = mx;
    this.lastMoveY = my;
    this.lastMoveTime = performance.now();
  }

  drag(mx: number, my: number): void {
    if (!this.isDragging) return;
    const now = performance.now();
    const dt = Math.max(1, now - this.lastMoveTime);

    this.vx = ((mx - this.lastMoveX) / dt) * 16;
    this.vy = ((my - this.lastMoveY) / dt) * 16;

    this.lastMoveX = mx;
    this.lastMoveY = my;
    this.lastMoveTime = now;

    this.targetX = this.dragCamX + (mx - this.dragStartX);
    this.targetY = this.dragCamY + (my - this.dragStartY);
    this.x = this.targetX;
    this.y = this.targetY;
  }

  endDrag(): void {
    this.isDragging = false;
    const timeSinceMove = performance.now() - this.lastMoveTime;
    if (timeSinceMove > 100) {
      this.vx = 0;
      this.vy = 0;
    }
  }

  /**
   * Zoom at a screen point. The world point under (mx, my)
   * stays fixed throughout the animated zoom.
   */
  zoomAt(mx: number, my: number, delta: number): void {
    // Scale zoom proportionally to scroll distance for smooth trackpad zooming.
    // Clamp to prevent extreme jumps from fast scrolling.
    const clamped = Math.max(-200, Math.min(200, delta));
    const factor = Math.exp(-clamped * 0.0025);
    const newScale = Math.max(
      this.minScale,
      Math.min(this.maxScale, this.targetScale * factor),
    );
    if (newScale === this.targetScale) return;

    // Record the world-space point under cursor using rendered state
    // (what the user actually sees) so the anchor stays visually correct
    this.zoomAnchorWX = (mx - this.x) / this.scale;
    this.zoomAnchorWY = (my - this.y) / this.scale;
    this.zoomAnchorSX = mx;
    this.zoomAnchorSY = my;
    this.isZooming = true;

    this.targetScale = newScale;
    this.targetX = this.zoomAnchorSX - this.zoomAnchorWX * this.targetScale;
    this.targetY = this.zoomAnchorSY - this.zoomAnchorWY * this.targetScale;
  }

  /**
   * For pinch zoom: set scale and position directly.
   * No easing since fingers provide continuous input.
   */
  pinchZoomAt(cx: number, cy: number, newScale: number): void {
    newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));
    const wx = (cx - this.x) / this.scale;
    const wy = (cy - this.y) / this.scale;
    this.scale = newScale;
    this.targetScale = newScale;
    this.x = cx - wx * this.scale;
    this.y = cy - wy * this.scale;
    this.targetX = this.x;
    this.targetY = this.y;
    this.isZooming = false;
  }

  /** Pan offset applied directly (for two-finger drag during pinch). */
  panBy(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
    this.targetX = this.x;
    this.targetY = this.y;
  }

  private clampBounds(viewW: number, viewH: number): void {
    const sw = this.worldSize * this.targetScale;
    const sh = this.worldSize * this.targetScale;
    const pad = Math.max(this.boundsPadding, Math.min(viewW, viewH) * 0.25);

    if (sw < viewW) {
      this.targetX = (viewW - sw) / 2;
    } else {
      this.targetX = Math.min(pad, Math.max(viewW - sw - pad, this.targetX));
    }
    if (sh < viewH) {
      this.targetY = (viewH - sh) / 2;
    } else {
      this.targetY = Math.min(pad, Math.max(viewH - sh - pad, this.targetY));
    }
  }

  update(viewW: number, viewH: number): void {
    // Dynamic min scale: allow zooming out to see the whole map with a
    // 20% border, but never below the tile-system floor (config.minZoom).
    this.minScale = Math.max(
      this.minScaleFloor,
      Math.min(viewW, viewH) / this.worldSize * 0.8,
    );

    // Clamp target scale within bounds
    if (this.targetScale < this.minScale) this.targetScale = this.minScale;
    if (this.targetScale > this.maxScale) this.targetScale = this.maxScale;

    // Inertia (only when not dragging)
    if (!this.isDragging) {
      this.targetX += this.vx;
      this.targetY += this.vy;
      this.vx *= this.friction;
      this.vy *= this.friction;
      if (Math.abs(this.vx) < 0.01) this.vx = 0;
      if (Math.abs(this.vy) < 0.01) this.vy = 0;
    }

    // Only clamp when not actively dragging so the user can freely
    // overshoot; bounds snap-back happens naturally after release via inertia.
    if (!this.isDragging) {
      this.clampBounds(viewW, viewH);
    }

    // Ease scale
    this.scale += (this.targetScale - this.scale) * this.zoomEasing;

    // While zoom is animating, derive position from anchor
    // so the world point under cursor never drifts
    if (this.isZooming) {
      this.x = this.zoomAnchorSX - this.zoomAnchorWX * this.scale;
      this.y = this.zoomAnchorSY - this.zoomAnchorWY * this.scale;

      if (Math.abs(this.scale - this.targetScale) < 0.001) {
        this.isZooming = false;
      }
    } else {
      // Normal pan easing
      this.x += (this.targetX - this.x) * this.panEasing;
      this.y += (this.targetY - this.y) * this.panEasing;
    }
  }

  /** Convert screen coordinates to world coordinates. */
  screenToWorld(sx: number, sy: number): { wx: number; wy: number } {
    return {
      wx: (sx - this.x) / this.scale,
      wy: (sy - this.y) / this.scale,
    };
  }

  /** Fit the entire world into the given viewport. */
  fitToView(viewW: number, viewH: number): void {
    const scaleX = viewW / this.worldSize;
    const scaleY = viewH / this.worldSize;
    this.targetScale = Math.min(scaleX, scaleY);
    this.scale = this.targetScale;
    this.targetX = (viewW - this.worldSize * this.targetScale) / 2;
    this.targetY = (viewH - this.worldSize * this.targetScale) / 2;
    this.x = this.targetX;
    this.y = this.targetY;
    this.isZooming = false;
  }
}
